//-- Copyright (c) 2021 TMT Insights Group, LLC
import { ChangeEvent, useContext, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Dialog } from '@/components/_common';
import { useToast } from '@/components/_common/Toast';
import PolarisIcon, { IconDictionary } from '@/components/_common/PolarisIcon';
import { DialogContentText, TextField } from '@mui/material';
import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectCurrentTableBulkActions } from '@/redux/selectors/dataTableSelectors';
import { capitalizeNoun, displayBulkActionIdent } from '@/_utils/utils';
import SchemaForm from "@/components/_common/SchemaForm/SchemaForm";
import { formatFormValues } from '@/components/_common/BulkActionDialog';
import axios from '@/_utils/axios';
import { setBulkErrors } from '@/redux/slices/dataTableSlice';

type EntityActionDialogProps = {
  id: string | number;
  entity: string;
  action: string | null;
  overrideActions?: any;
  isOpen: boolean;
  setIsOpen: (value: string | null) => void;
  afterSubmit: () => void;
};

const EntityActionDialog = ({ id, entity, action, isOpen, setIsOpen, overrideActions, afterSubmit }: EntityActionDialogProps) => {
  const { success, error } = useToast();
  const dispatch = useAppDispatch();
  const bulkActions = useAppSelector(selectCurrentTableBulkActions);
  const bulkActionIcons = useContext(ConfigContext)?.bulkActionIcons;
  const [status_reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const formRef = useRef<any>(null);
  const actions = overrideActions || bulkActions;
  const currentAction = useMemo(() => (actions && action ? actions[action] : null), [actions, action]);
  const isCancelAction = currentAction?.ident === 'cancel';
  const hasStatusReason = !!currentAction?.action_schema?.properties?.status_reason;

  const handleReasonInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setReason(e.target.value);
  };

  const handleCancel = () => {
    setIsOpen(null);
  };

  const handleSubmit = async () => {
    if (!currentAction) return;
    if (formRef.current && !formRef.current.validateForm()) {
      return;
    }

    setLoading(true);
    const label = displayBulkActionIdent(currentAction.ident);

    try {
      const formattedValues = formatFormValues(formRef.current?.state?.formData ?? {});
      let requestBody: any = { resource_identifiers: [id] };
      if (isCancelAction) {
        requestBody['action_payload'] = { status_reason };
      }
      if (formattedValues && Object.keys(formattedValues).length > 0) {
        requestBody = {
          ...requestBody,
          action_payload: {
            ...(requestBody.action_payload ?? {}),
            ...formattedValues
          }
        };
      }

      const { data: res } = await axios.post(`bulk_actions/${currentAction.action_type}/${currentAction.ident}/`, requestBody, {
        baseURL: `/api/v${currentAction?.version || 1}/`
      });
      if (res?.data?.queued_ids?.length) {
        success(res?.message?.length ? res?.message : `${res?.data?.queue_ids.length} ${entity}(s) submitted to ${label}`);
      }
      const ineligibleIds = Object.keys(res?.data?.ineligible_ids ?? {}).map((key) => [key, res?.data?.ineligible_ids[key]]);
      if (ineligibleIds.length) {
        dispatch(setBulkErrors(ineligibleIds));
      }
      afterSubmit();
    } catch (err) {
      console.error(`Failed to bulk ${label} ${entity}`, err);
      error(`Failed bulk ${label} ${entity}`);
    } finally {
      setIsOpen(null);
      setLoading(false);
    }
  };

  return (
    <StyledDialog
      title={`Request ${capitalizeNoun(entity)} ${currentAction ? displayBulkActionIdent(currentAction.ident) : ''}`}
      open={isOpen}
      titleIcon={
        <PolarisIcon
          icon={
            currentAction
              ? currentAction.icon ||
                bulkActionIcons?.[currentAction.ident] ||
                (IconDictionary[currentAction.ident] && typeof IconDictionary[currentAction.ident] === 'string'
                  ? IconDictionary[currentAction.ident]
                  : 'edit')
              : 'edit'
          }
          height={19}
          style={{ opacity: 0.5, marginRight: 8 }}
        />
      }
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      cancelText="Cancel"
      submitText={`${currentAction ? `${displayBulkActionIdent(currentAction.ident)} ${capitalizeNoun(entity)}` : 'Submit'}`}
      loading={loading}
    >
      <DialogContentText>This action cannot be undone once completed. Are you sure you want to continue?</DialogContentText>
      {isCancelAction && !hasStatusReason ? (
        <TextField
          fullWidth
          id="reason"
          label={`Reason${status_reason.length > 200 ? `: ${status_reason.length} / 255 characters` : ''}`}
          multiline
          minRows={4}
          maxRows={8}
          value={status_reason}
          onChange={handleReasonInputChange}
          sx={{ mt: '1rem' }}
          inputProps={{ maxLength: 255 }}
        />
      ) : null}
      {currentAction?.action_schema ? (
        <SchemaFormWrapper>
          <SchemaForm isInsideModal formRef={formRef} schema={currentAction?.action_schema || {}} />
        </SchemaFormWrapper>
      ) : null}
    </StyledDialog>
  );
};

export default EntityActionDialog;

const StyledDialog = styled(Dialog)(() => ({
  '& .MuiPaper-root': {
    maxWidth: 450
  }
}));

const SchemaFormWrapper = styled.div`
  margin-top: ${(props) => props.theme.spacing(3.5)};
`;
